import { useState } from 'react';

export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(false);

    if (formData.message.length > 500) {
      setError(true);
      setIsSubmitting(false);
      return;
    }

    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        subject: formData.subject,
        message: formData.message,
      });

      const res = await fetch('https://readdy.ai/api/form/d6mojbtv117fnkj2h870', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
      <div className="absolute top-0 right-0 w-48 md:w-96 h-48 md:h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-48 md:w-96 h-48 md:h-96 bg-pink-500/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="text-center mb-12 md:mb-16">
          <span className="inline-block px-4 md:px-6 py-2 rounded-full bg-gradient-to-r from-pink-500/20 via-purple-500/20 to-blue-500/20 border border-blue-500/30 text-blue-400 text-sm font-semibold tracking-wider mb-4 md:mb-6">
            CONTACT US
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-4 md:mb-6" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
            お<span className="bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">問い合わせ</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
            企画のご相談、出演に関するお問い合わせなど、<br className="hidden md:block" />
            お気軽にご連絡ください。
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Contact Info */}
            <div className="space-y-6 md:space-y-8">
              <div className="bg-gray-900/80 rounded-2xl md:rounded-3xl p-6 md:p-8 border border-gray-800">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6">コンタクト情報</h3>
                <div className="space-y-4 md:space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-gradient-to-r from-pink-500/20 to-purple-500/20 flex-shrink-0">
                      <i className="ri-mail-line text-lg md:text-xl text-pink-400"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">メール</h4>
                      <p className="text-gray-400 text-sm md:text-base">ufirst2025@gmail.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-gradient-to-r from-purple-500/20 to-blue-500/20 flex-shrink-0">
                      <i className="ri-phone-line text-lg md:text-xl text-purple-400"></i>
                    </div>
                    <div>
                      <h4 className="text-white font-medium mb-1">電話</h4>
                      <p className="text-gray-400 text-sm md:text-base">080-4356-0612</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-900/80 rounded-2xl md:rounded-3xl p-6 md:p-8 border border-gray-800">
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6">営業時間</h3>
                <div className="space-y-2 md:space-y-3">
                  <div className="flex justify-between text-sm md:text-base">
                    <span className="text-gray-400">平日</span>
                    <span className="text-white">10:00 - 18:00</span>
                  </div>
                  <div className="flex justify-between text-sm md:text-base">
                    <span className="text-gray-400">土曜日</span>
                    <span className="text-white">10:00 - 15:00</span>
                  </div>
                  <div className="flex justify-between text-sm md:text-base">
                    <span className="text-gray-400">日曜・祝日</span>
                    <span className="text-gray-500">休業</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-gray-900/80 rounded-2xl md:rounded-3xl p-6 md:p-8 border border-gray-800">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6">メッセージを送る</h3>

              {submitted && (
                <div className="mb-4 md:mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400 text-sm md:text-base">
                  <i className="ri-check-line mr-2"></i>
                  メッセージが送信されました。担当者よりご連絡いたします。
                </div>
              )}

              {error && (
                <div className="mb-4 md:mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm md:text-base">
                  <i className="ri-error-warning-line mr-2"></i>
                  送信に失敗しました。時間をおいて再度お試しください。
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form className="space-y-4 md:space-y-6">
                <div>
                  <label className="block text-white text-sm font-medium mb-2">お名前</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm md:text-base focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition-colors"
                    placeholder="山田太郎"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">メールアドレス</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm md:text-base focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition-colors"
                    placeholder="example@email.com"
                  />
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">要件</label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm md:text-base focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition-colors"
                  >
                    <option value="">選択してください</option>
                    <option value="出演者募集について">出演者募集について</option>
                    <option value="コラボレーション提案">コラボレーション提案</option>
                    <option value="メディア取材">メディア取材</option>
                    <option value="その他">その他</option>
                  </select>
                </div>

                <div>
                  <label className="block text-white text-sm font-medium mb-2">
                    メッセージ
                    <span className="ml-2 text-gray-500 font-normal">({formData.message.length}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm md:text-base focus:border-pink-500 focus:ring-1 focus:ring-pink-500 transition-colors resize-none"
                    placeholder="お問い合わせ内容をご記入ください..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || formData.message.length > 500}
                  className="w-full py-3 md:py-4 bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-pink-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      送信中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      メッセージを送信
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
